import i18n from '@/libs/i18n'
import type { IChatOperation, IChatOperationOutcome } from '@/types/Chat'

/**
 * What a confirmed proposal did, said in the reader's own language.
 *
 * The desk read "Izvedeno — moved 1 stays" on 2026-09-05: an English half-sentence with
 * an English plural, on the one line that tells somebody whether the thing they pressed
 * happened. The service builds its result strings in Go and has no i18n runtime
 * (CLAUDE.md deviation 24), so the repair is not to translate over there — it is to
 * stop reading a sentence at all. The API now sends a CODE and a COUNT and this maps
 * them.
 *
 * The English sentence stays as the fallback, for two cases that are both real: an
 * operation confirmed before this existed, and a code from an API newer than this
 * portal. Showing the audit line is worse than a translation and much better than a
 * blank.
 *
 * Nothing here is per-kind and nothing should become so: a code is resolved as a key,
 * so a new outcome is a pair of locale entries and no code at all. The one thing worth
 * knowing about the folio codes is why `discount_applied` and `discount_pending` are
 * two of them: over the confirming account's own ceiling a discount takes NOTHING off
 * the bill and waits for somebody senior, and a single line saying "done" for both
 * would be a price read out to a guest that the house has not agreed to.
 */

/**
 * Slovenian counts in four forms and the portal's i18n does not know it.
 *
 * vue-i18n's own pluralisation is left alone deliberately: ten messages in sl.ts
 * already use its two- and three-choice syntax under the DEFAULT rule, and teaching
 * the instance a Slovenian rule would silently re-pick every one of them. So the form
 * is chosen here and named as a key.
 *
 * The rule is the CLDR one: 101 is "one" (101 bivanje) and 102 is "two", which is why
 * it counts modulo a hundred rather than testing the number itself.
 */
const slovenePluralForm = (count: number): string => {
    const rest = Math.abs(Math.trunc(count)) % 100

    if (rest === 1) return 'one'
    if (rest === 2) return 'two'
    if (rest === 3 || rest === 4) return 'few'

    return 'other'
}

/** Everything else here counts as English does. */
const pluralForm = (locale: string, count: number): string =>
    locale === 'sl' ? slovenePluralForm(count) : (Math.abs(count) === 1 ? 'one' : 'other')

/**
 * Resolves one key, or nothing.
 *
 * vue-i18n answers a missing key with the key itself, which is what a screen showing
 * "assistant_result.stays_moved.two" is made of. Comparing the answer to the key is how
 * that is caught before it reaches a wall.
 */
const translated = (key: string, values: Record<string, unknown>): string => {
    const line = i18n.global.t(key, values)

    return line === key ? '' : line
}

/**
 * The result line for one outcome, or the fallback when nothing is known about the code.
 *
 * A code carries at most `one` / `two` / `few` / `other`; the kinds that count nothing —
 * one letter, one room's order — carry only `other`, so the chosen form falls back to it
 * rather than every code having four identical strings.
 */
export const chatOutcomeLine = (outcome?: IChatOperationOutcome | null, fallback = ''): string => {
    if (!outcome?.code) return fallback

    const locale = String(i18n.global.locale.value)
    const values = { count: outcome.count, detail: outcome.detail }

    const line = translated(`assistant_result.${outcome.code}.${pluralForm(locale, outcome.count)}`, values)
        || translated(`assistant_result.${outcome.code}.other`, values)

    if (!line) return fallback

    // The detail is a fact with no key of its own — a reservation number, an address,
    // the stays that would not move. Messages that want it name {detail} and already
    // carry it; the rest get it appended rather than dropped.
    return outcome.detail && !line.includes(outcome.detail) ? `${line} — ${outcome.detail}` : line
}

/** The same for a whole operation, which is where the pre-outcome fallback lives. */
export const chatOperationResult = (operation: IChatOperation): string =>
    chatOutcomeLine(operation.outcome, operation.result)
